/*
 * Attendance reporting for organisers.
 *
 * Two questions, in the order they get asked: how many came to each session, and who
 * exactly. The first is the table; the second is the export, because a list of 161 names
 * is not something anyone reads on a phone - it is something they open in Excel.
 *
 * Event-wide on purpose. The other staff screens are scoped to the caller's `qr_venue`
 * rooms because they are room-steward tools; this is not one, and "who is short of the
 * threshold" cannot be answered a room at a time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, RefreshCw, UserPlus, Users } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import type { AttendanceSummarySlot } from '../lib/types'
import { useAuthed } from '../lib/session'
import { attendanceCsv, csvFilename, downloadCsv } from '../lib/attendance-csv'
import { EVENT } from '../config/event'
import { Masthead } from '../ui/Chrome'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { Sheet } from '../ui/Sheet'
import { Notice, Skeleton, useToast } from '../ui/Feedback'

export function AttendanceReportRoute() {
  const session = useAuthed()
  const [slots, setSlots] = useState<AttendanceSummarySlot[] | null>(null)
  const [failed, setFailed] = useState(false)
  /* Which export is in flight: a slot id, 'all', or nothing. */
  const [busy, setBusy] = useState<number | 'all' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const toast = useToast()

  /*
   * Recording a check-in by hand. For the delegate whose phone is dead or who lost
   * their session in the queue. The server applies the same track rule as a scan and
   * logs who recorded it; this sheet is only the way to ask.
   */
  const [manualFor, setManualFor] = useState<AttendanceSummarySlot | null>(null)
  const [manualEmail, setManualEmail] = useState('')
  const [manualError, setManualError] = useState<string | null>(null)
  const [manualBusy, setManualBusy] = useState(false)

  async function recordManual() {
    if (!manualFor || manualBusy) return
    const target = manualEmail.trim()
    if (!target.includes('@')) { setManualError('Enter the participant\'s registered email.'); return }
    setManualBusy(true)
    setManualError(null)
    try {
      const res = await api.manualCheckIn(session.email, session.at, manualFor.id, target)
      toast.show(res.duplicate ? 'Already checked in' : `Recorded for ${res.participant_email}`, 'ok')
      setManualFor(null)
      setManualEmail('')
      load()
    } catch (err) {
      const reason = err instanceof ApiError ? err.body?.reason : undefined
      setManualError(
        reason === 'not_participant' ? 'No participant with that email on this event.'
          : reason === 'not_on_track' ? 'That session is not on this participant\'s track, or needs no check-in.'
            : err instanceof ApiError && err.status === 403 ? 'This account is not allowed to record check-ins.'
              : 'Could not record it. Try again.',
      )
    } finally {
      setManualBusy(false)
    }
  }

  const load = useCallback(() => {
    setFailed(false)
    api.attendanceSummary(session.email, session.at)
      .then((res) => setSlots(res.slots ?? []))
      .catch(() => setFailed(true))
  }, [session.email, session.at])

  useEffect(load, [load])

  /*
   * Only the sessions that take a check-in. A meal has no attendance to report and would
   * sit at 0 of 0 forever, which reads as a problem rather than as "not applicable".
   */
  const rows = useMemo(
    () => (slots ?? []).filter((s) => s.qr_required),
    [slots],
  )

  /*
   * Sessions with a cohort, counted separately from sessions that merely exist.
   *
   * A multi-track event carries slots for tracks nobody has registered on yet - 14 of
   * event 6's 26 check-in sessions, at the time of writing. Those have `expected: 0`,
   * and counting them in "across N sessions" makes the headline describe a scale of
   * event that is not happening.
   */
  const withCohort = useMemo(() => rows.filter((s) => s.expected > 0), [rows])

  const totals = useMemo(() => {
    const attended = withCohort.reduce((n, s) => n + s.attended, 0)
    const expected = withCohort.reduce((n, s) => n + s.expected, 0)
    return { attended, expected, pct: expected ? Math.round((attended / expected) * 100) : 0 }
  }, [withCohort])

  async function exportRows(slot: AttendanceSummarySlot | null) {
    setBusy(slot ? slot.id : 'all')
    setError(null)
    try {
      const res = await api.attendanceDetail(session.email, session.at, slot?.id)
      if (!res.rows.length) {
        setError(slot ? 'Nobody has checked in to that session yet.' : 'No check-ins recorded yet.')
        return
      }
      downloadCsv(
        attendanceCsv(res.rows),
        csvFilename(slot ? `${EVENT.shortName} ${slot.slot_name}` : `${EVENT.shortName} attendance`),
      )
    } catch {
      setError('Could not build the export. Nothing was downloaded.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <main className="page report-page">
      <Masthead kicker="Organiser" title="Attendance" />

      {slots === null && !failed ? <Skeleton height="14rem" /> : null}

      {failed ? (
        <Notice
          icon={<Users size={22} strokeWidth={1.3} />}
          title="Could not load the report"
          body="The server did not answer. Your check-in data is unaffected."
          action={{ label: 'Try again', onClick: load }}
        />
      ) : null}

      {slots !== null && rows.length === 0 ? (
        <Notice
          icon={<Users size={22} strokeWidth={1.3} />}
          title="No sessions take a check-in"
          body="Only sessions marked as requiring a QR check-in appear here."
        />
      ) : null}

      {rows.length > 0 ? (
        <>
          {/* The headline first: one number for the event, then the breakdown. */}
          <section className="report-total">
            <div>
              <p className="kicker">Check-ins recorded</p>
              <p className="report-total__figure tnum">
                {totals.attended} <span>/ {totals.expected}</span>
              </p>
              <p className="report-total__note">
                across {withCohort.length} session{withCohort.length === 1 ? '' : 's'} · {totals.pct}% of expected
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              busy={busy === 'all'}
              icon={<Download size={16} strokeWidth={1.6} />}
              onClick={() => void exportRows(null)}
            >
              Export all
            </Button>
          </section>

          {error ? <p className="field__error report-error" role="alert">{error}</p> : null}

          <ul className="report-list">
            {rows.map((slot) => {
              /*
               * No cohort is not zero attendance. A slot on a track nobody has
               * registered on would otherwise draw an empty bar reading 0/0, which
               * says "nobody came" about a session nobody was ever due at - and a
               * screen full of those is how an organiser concludes the event failed.
               */
              const noCohort = slot.expected === 0
              const pct = noCohort ? 0 : Math.round((slot.attended / slot.expected) * 100)
              return (
                <li className="report-row" key={slot.id} data-empty={noCohort || undefined}>
                  <div className="report-row__main">
                    <p className="report-row__title">{slot.slot_name}</p>
                    <p className="report-row__meta">
                      {slot.slot_date} · {slot.slot_time}
                      {slot.venue_name ? ` · ${slot.venue_name}` : ''}
                    </p>
                    {/*
                      * A bar, not just a percentage. Twelve rows of numbers is a table
                      * you have to read; a bar is one you can scan for the session that
                      * went wrong, which is the whole point of the screen.
                      */}
                    {noCohort ? (
                      <p className="report-row__empty">No one is registered on this track</p>
                    ) : (
                      <div
                        className="report-row__bar"
                        role="img"
                        aria-label={`${slot.attended} of ${slot.expected} attended, ${pct}%`}
                      >
                        <span style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="report-row__side">
                    <p className="report-row__count tnum">
                      {noCohort
                        ? <span aria-label="No cohort">&mdash;</span>
                        : <>{slot.attended}<span>/{slot.expected}</span></>}
                    </p>
                    {slot.qr_required ? (
                      <button
                        type="button"
                        className="report-row__export"
                        disabled={noCohort}
                        onClick={() => { setManualFor(slot); setManualEmail(''); setManualError(null) }}
                        aria-label={`Record a check-in by hand for ${slot.slot_name}`}
                      >
                        <UserPlus size={15} strokeWidth={1.7} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="report-row__export"
                      disabled={busy === slot.id || noCohort}
                      onClick={() => void exportRows(slot)}
                      aria-label={`Export attendees of ${slot.slot_name}`}
                    >
                      {busy === slot.id
                        ? <RefreshCw size={15} strokeWidth={1.7} className="report-row__spin" />
                        : <Download size={15} strokeWidth={1.7} />}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>

          <p className="report-foot">
            Exports open in Excel. Each row is one person at one session, with their
            check-in time. The person icon records a check-in by hand, for a delegate
            whose phone could not scan; it is logged against your account.
          </p>

          <Sheet
            open={manualFor !== null}
            onClose={() => { if (!manualBusy) setManualFor(null) }}
            title="Record a check-in"
          >
            <form
              onSubmit={(event) => { event.preventDefault(); void recordManual() }}
              style={{ display: 'grid', gap: 'var(--space-md)' }}
            >
              <p className="muted" style={{ margin: 0, fontSize: 'var(--step--1)' }}>
                {manualFor?.slot_name}
                {manualFor?.venue_name ? ` · ${manualFor.venue_name}` : ''}
              </p>
              <Field
                label="Participant email"
                type="email"
                inputMode="email"
                autoComplete="off"
                value={manualEmail}
                error={manualError}
                hint="The address they registered with. This counts exactly like a scan and is recorded as entered by you."
                onChange={(event) => { setManualEmail(event.target.value); setManualError(null) }}
                autoFocus
              />
              <Button type="submit" variant="primary" busy={manualBusy} disabled={!manualEmail.trim()}>
                Record check-in
              </Button>
            </form>
          </Sheet>
        </>
      ) : null}
    </main>
  )
}
